<template>
    <a-modal :visible="true" width="70vw" :footer="null" :closable="false" centered wrap-class-name="apm-wrap" @cancel="$emit('close')">
        <div class="apm-modal">
            <div class="apm-head">
                <div>
                    <h3>Adicionar produto</h3>
                </div>
                <button class="apm-x" @click="$emit('close')">
                    <span class="material-icons-outlined">close</span>
                </button>
            </div>

            <div class="apm-body">
                <a-input-search
                    ref="term"
                    v-model="term"
                    placeholder="EAN, código interno ou descrição do produto"
                    enter-button="Buscar"
                    :loading="searching"
                    @search="search(1)"
                />

                <div class="apm-scroll">
                    <a-table
                        class="apm-table"
                        size="small"
                        :row-key="(row) => row.product.id"
                        :row-selection="rowSelection"
                        :columns="columns"
                        :data-source="results"
                        :loading="searching"
                        :pagination="false"
                        :locale="{ emptyText: 'Nenhum produto encontrado.' }"
                    >
                        <template slot="description" slot-scope="text, row">
                            <span class="apm-name">{{ row.product.description }}</span>
                            <a-tag v-if="regimeOf(row)" class="apm-regime" :color="regimeOf(row).color">{{ regimeOf(row).label }}</a-tag>
                        </template>
                        <template slot="lastOffer" slot-scope="text, row">
                            <span v-if="row.last_offer">{{ brl(row.last_offer.price) }} · {{ num(row.last_offer.quantity) }}</span>
                            <span v-else>--</span>
                        </template>
                        <template v-for="field in fields" :slot="field" slot-scope="text, row">
                            <a-input
                                v-if="actionOf(row) !== 'listed'"
                                :key="`${field}-${row.product.id}`"
                                size="small"
                                :class="`apm-input apm-input-${field}`"
                                :value="inputOf(row, field)"
                                :prefix="field === 'price' ? 'R$' : undefined"
                                :inputmode="field === 'price' ? 'numeric' : 'decimal'"
                                :placeholder="field === 'price' ? '0,00' : '0'"
                                :maxlength="14"
                                @input="handleInput($event, row, field)"
                                @pressEnter="select(row)"
                            />
                            <span v-else :key="`${field}-${row.product.id}`">{{ listedValue(row, field) }}</span>
                        </template>
                        <template slot="situacao" slot-scope="text, row">
                            <span v-if="actionOf(row) === 'listed'" class="apm-added">Na requisição</span>
                            <span v-else-if="actionOf(row) === 'order'" class="apm-added">Não ofertado</span>
                            <span v-else>--</span>
                        </template>
                    </a-table>
                </div>

                <a-pagination
                    v-if="pagination"
                    class="apm-pagination"
                    v-bind="pagination"
                    @change="pageChange"
                />
            </div>

            <div class="apm-foot">
                <span v-if="selectedIds.length && !allReady" class="apm-warning">Informe quantidade e preço maiores que zero nos produtos marcados.</span>
                <a-button :disabled="adding" @click="$emit('close')">Fechar</a-button>
                <a-button type="primary" :loading="adding" :disabled="!selectedIds.length || !allReady" @click="submitSelected">
                    Adicionar selecionados{{ selectedIds.length ? ` (${selectedIds.length})` : "" }}
                </a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import SellerOrderService from "@/services/v3/seller-orders/seller-order.service";
import visitFormat from "@/pages/visits/visit-format.mixin";
import { PURCHASE_REGIMES } from "../seller-order-status";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";

const PER_PAGE = 30;

const toText = (value) => (value === null || value === undefined ? "" : String(value).replace(".", ","));

const toNumber = (text) => {
    const value = parseFloat(String(text || "").replace(",", "."));
    return Number.isFinite(value) ? value : null;
};

const formatPrice = (value) => Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parsePrice = (text) => Number(String(text || "").replace(/\D/g, "")) / 100;

const ownOf = (line, field) => {
    if (!line) return null;
    const own = line[`client_${field}`];
    return own === null || own === undefined ? line[field] : own;
};

export default {
    name: "SellerOrderAddProductModal",
    mixins: [visitFormat, loaderMixin],
    props: {
        order: { type: Object, required: true },
        clientOwned: { type: Boolean, default: false },
    },
    data() {
        return {
            service: new SellerOrderService(),
            fields: ["quantity", "price"],
            term: "",
            appliedTerm: "",
            results: [],
            meta: null,
            searching: false,
            adding: false,
            inputs: {},
            selected: {},
            searchSeq: 0,
        };
    },
    computed: {
        columns() {
            return [
                { title: "EAN", key: "ean", width: 140, customRender: (t, row) => row.product.ean || "--" },
                { title: "Código Interno", key: "internal_code", width: 120, customRender: (t, row) => row.product.internal_code || "--" },
                { title: "Nome", key: "description", scopedSlots: { customRender: "description" } },
                { title: "Embalagem", key: "packing", width: 110, customRender: (t, row) => row.product.packing || "--" },
                { title: "Última oferta", key: "lastOffer", width: 140, align: "right", scopedSlots: { customRender: "lastOffer" } },
                { title: "Qtd.", key: "quantity", width: 100, align: "center", scopedSlots: { customRender: "quantity" } },
                { title: "Preço", key: "price", width: 140, align: "center", scopedSlots: { customRender: "price" } },
                { title: "Situação", key: "situacao", width: 120, align: "center", scopedSlots: { customRender: "situacao" } },
            ];
        },
        selectedIds() {
            return Object.keys(this.selected).map(Number);
        },
        allReady() {
            return Object.values(this.selected).every((row) => this.ready(row));
        },
        rowSelection() {
            return {
                selectedRowKeys: this.selectedIds,
                onSelect: (row, checked) => this.toggle(row, checked),
                onSelectAll: (checked, _rows, changed) => changed.forEach((row) => this.toggle(row, checked)),
                getCheckboxProps: (row) => ({ props: { disabled: this.actionOf(row) === "listed" || this.adding } }),
            };
        },
        pagination() {
            if (!this.meta) return false;
            return {
                current: this.meta.current_page,
                pageSize: this.meta.per_page,
                total: this.meta.total,
                size: "small",
                showTotal: (count) => `${count} ${count === 1 ? "produto" : "produtos"}`,
            };
        },
    },
    mounted() {
        this.search(1);
        this.$nextTick(() => this.$refs.term && this.$refs.term.focus());
    },
    methods: {
        search(page) {
            const term = this.term.trim();
            const isEan = /^\d{8,20}$/.test(term);
            this.appliedTerm = term;
            const params = isEan ? { ean: term } : { ...(term ? { q: term } : {}), page, per_page: PER_PAGE };
            const seq = ++this.searchSeq;
            this.searching = true;
            this.service
                .searchProducts(this.order.id, params)
                .then(({ data }) => {
                    if (seq !== this.searchSeq) return;
                    this.results = data.data || [];
                    this.meta = data.meta || null;
                })
                .catch((error) => {
                    if (seq !== this.searchSeq) return;
                    // LEGACY: back sem busca paginada recusa a busca vazia (422). Remover quando o kyojin com a busca paginada estiver em produção.
                    if (!term && error.response && error.response.status === 422) {
                        this.results = [];
                        this.meta = null;
                        return;
                    }
                    this.notifyError(error, "Não foi possível buscar os produtos.");
                })
                .finally(() => {
                    if (seq === this.searchSeq) this.searching = false;
                });
        },
        pageChange(page) {
            this.term = this.appliedTerm;
            this.search(page);
        },
        regimeOf(row) {
            return PURCHASE_REGIMES[row.purchase_regime] || null;
        },
        actionOf(row) {
            if (!row.line) return "add";
            if (this.clientOwned || row.line.status === "OFFERED") return "listed";
            return "order";
        },
        listedValue(row, field) {
            const value = ownOf(row.line, field);
            if (value === null || value === undefined) return "--";
            return field === "price" ? this.brl(value) : this.num(value);
        },
        defaultOf(row, field) {
            const own = ownOf(row.line, field);
            if (field === "price") {
                const suggested = row.line && row.line.suggested_price;
                const price = own !== null && own !== undefined ? own : suggested || (row.last_offer ? row.last_offer.price : null);
                return price === null || price === undefined ? "" : formatPrice(price);
            }
            return own !== null && own !== undefined ? toText(own) : "";
        },
        inputOf(row, field) {
            const values = this.inputs[row.product.id];
            return values && field in values ? values[field] : this.defaultOf(row, field);
        },
        toggle(row, checked) {
            if (checked && this.actionOf(row) !== "listed") this.$set(this.selected, row.product.id, row);
            else this.$delete(this.selected, row.product.id);
        },
        select(row) {
            this.toggle(row, true);
        },
        handleInput(event, row, field) {
            let value;
            if (field === "price") {
                value = formatPrice(parsePrice(event.target.value));
            } else {
                value = event.target.value.replace(/\./g, ",").replace(/[^\d,]/g, "");
                const comma = value.indexOf(",");
                if (comma !== -1) value = value.slice(0, comma + 1) + value.slice(comma + 1).replace(/,/g, "").slice(0, 3);
            }
            event.target.value = value;
            this.$set(this.inputs, row.product.id, { ...(this.inputs[row.product.id] || {}), [field]: value });
            if (field === "quantity" && toNumber(value) > 0) this.toggle(row, true);
        },
        valuesOf(row) {
            return { quantity: toNumber(this.inputOf(row, "quantity")), price: parsePrice(this.inputOf(row, "price")) };
        },
        ready(row) {
            const { quantity, price } = this.valuesOf(row);
            return quantity > 0 && price > 0;
        },
        async submitSelected() {
            if (this.adding || !this.selectedIds.length || !this.allReady) return;
            this.adding = true;
            let last = null;
            let failed = 0;
            for (const row of Object.values(this.selected)) {
                const { quantity, price } = this.valuesOf(row);
                try {
                    const { data } = await this.service.addProduct(this.order.id, { product_id: row.product.id, origin: "SEARCH", quantity, price });
                    row.line = data.line;
                    last = data;
                    this.$delete(this.inputs, row.product.id);
                    this.$delete(this.selected, row.product.id);
                } catch (error) {
                    failed += 1;
                    if (failed === 1) this.notifyError(error, "Não foi possível adicionar todos os produtos.");
                }
            }
            this.adding = false;
            if (last) this.$emit("added", last);
            if (last && !failed) {
                this.snack("Produtos adicionados à requisição.");
                this.$emit("close");
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.apm-modal {
    background: #fff;
    display: flex;
    flex-direction: column;
}
.apm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
        margin: 0;
    }
}
.apm-x {
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: #f0f0f0;
        color: #262626;
    }
}
.apm-body {
    padding: 12px 20px 16px;
    border-top: 1px solid #f0f0f0;
}
.apm-scroll {
    margin-top: 12px;
    max-height: 45vh;
    overflow: auto;
}
.apm-table ::v-deep table {
    width: max-content;
    min-width: 100%;
}
.apm-table ::v-deep .ant-table-thead > tr > th {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #fafafa !important;
    box-shadow: inset 0 -1px 0 #e8e8e8;
}
.apm-pagination {
    margin-top: 12px;
    text-align: right;
}
.apm-name {
    white-space: nowrap;
}
.apm-regime {
    margin-left: 6px;
}
.apm-input {
    text-align: center;
}
.apm-input-quantity {
    width: 80px;
}
.apm-input-price {
    width: 120px;
}
.apm-added {
    font-size: 12px;
    color: #8c8c8c;
}
.apm-warning {
    margin-right: auto;
    font-size: 12px;
    color: #cf1322;
}
.apm-foot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.apm-wrap {
    .ant-modal {
        min-width: 640px;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
